"use client";

// Site chatbot: floating launcher → chat panel. Answers come exclusively from
// the website's own content (via /api/chatbot → lib/chatbotKb). During the
// conversation the bot collects name → mobile → email and stores the lead in
// website_leads with the chat transcript. Hidden on /admin.
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

type Msg = {
  role: "bot" | "user";
  text: string;
  link?: string;
  linkLabel?: string;
  /** Extra outbound links, e.g. the two app stores for room booking. */
  links?: { href: string; label: string }[];
};
type LeadField = "name" | "phone" | "email" | null;

const QUICK = ["Our solutions", "Locations", "Pricing", "Contact details"];

const looksLikeQuestion = (t: string) =>
  t.includes("?") || t.trim().split(/\s+/).length > 5;

// crypto.randomUUID needs a secure context and is missing from older Android
// WebViews — i.e. the in-app browsers social links open in. Calling it
// unguarded threw "crypto.randomUUID is not a function" and took the whole
// page down with the error screen (seen in the website error log). The id only
// has to be unique per visit, so any random string will do.
function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the manual id */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

export function ChatWidget() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [lead, setLead] = useState<{ name?: string; phone?: string; email?: string }>({});
  const [pending, setPending] = useState<LeadField>(null);
  const [answered, setAnswered] = useState(0);
  const [leadSaved, setLeadSaved] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const sessionRef = useRef<string>("");
  const endRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ lead, pending, answered, leadSaved, msgs });
  stateRef.current = { lead, pending, answered, leadSaved, msgs };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs, open, busy]);

  // Admin on/off switch + a per-visit session id (ties the stored
  // conversation history together).
  useEffect(() => {
    sessionRef.current =
      sessionStorage.getItem("ts-chat-session") ||
      (() => {
        const id = randomId();
        sessionStorage.setItem("ts-chat-session", id);
        return id;
      })();
    fetch("/api/chatbot")
      .then((r) => r.json())
      .then((d: { enabled?: boolean }) => setEnabled(d.enabled !== false))
      .catch(() => setEnabled(true));
  }, []);

  if (pathname?.startsWith("/admin") || enabled === false || enabled === null) return null;

  const bot = (text: string, extra?: Partial<Msg>) =>
    setMsgs((m) => [...m, { role: "bot", text, ...extra }]);

  const openChat = () => {
    setOpen(true);
    if (msgs.length === 0) {
      bot(
        "Hi! I'm the Table Space assistant. Ask me anything about our workspaces, solutions or locations — everything I know comes from this website.",
      );
    }
  };

  const transcriptOf = (list: Msg[]) =>
    list.map((m) => `${m.role === "bot" ? "Bot" : "Visitor"}: ${m.text}`).join("\n");

  async function ask(question: string): Promise<void> {
    try {
      const res = await fetch("/api/chatbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "ask",
          message: question,
          sessionId: sessionRef.current,
          // history up to (but not including) this question — the server
          // appends the new exchange itself
          transcript: transcriptOf(stateRef.current.msgs.slice(0, -1)),
        }),
      });
      const d = (await res.json()) as {
        reply?: string; link?: string; linkLabel?: string;
        links?: { href: string; label: string }[];
      };
      bot(d.reply ?? "Sorry — something went wrong. Please try again.", {
        link: d.link,
        linkLabel: d.linkLabel,
        links: d.links,
      });
    } catch {
      bot("Sorry — I couldn't reach the server. Please try again.");
    }
  }

  // Ask for the next missing contact detail (after the visitor's questions
  // have been answered a couple of times, and one field per turn).
  function maybeAskLead(afterCount: number) {
    const { lead: l, leadSaved: saved } = stateRef.current;
    if (saved) return;
    if (!l.name && afterCount >= 1) {
      setPending("name");
      bot("By the way — may I know your name, so our team can assist you better?");
    } else if (l.name && !l.phone) {
      setPending("phone");
      bot(`Thanks, ${l.name}! Could you share your mobile number? Our workspace experts will reach out with the right options.`);
    } else if (l.name && l.phone && !l.email) {
      setPending("email");
      bot("And lastly, your work email? We'll send the details across.");
    }
  }

  async function saveLead(finalLead: { name?: string; phone?: string; email?: string }) {
    const transcript = transcriptOf(stateRef.current.msgs);
    try {
      const res = await fetch("/api/chatbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "lead", ...finalLead, transcript, sessionId: sessionRef.current }),
      });
      if (res.ok) {
        setLeadSaved(true);
        bot("Perfect — you're all set! Our team will get in touch shortly. Meanwhile, feel free to keep asking me about Table Space.");
      } else {
        bot("Hmm, I couldn't save that. Could you re-check the details?");
      }
    } catch {
      bot("Hmm, I couldn't save that just now — but you can always reach us via the contact page.");
    }
  }

  async function handleSend(raw?: string) {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);

    const { pending: field, lead: l } = stateRef.current;

    if (field === "name" && !looksLikeQuestion(text)) {
      const name = text.replace(/^(i am|i'm|my name is|this is)\s+/i, "").trim().slice(0, 80);
      setLead((p) => ({ ...p, name }));
      setPending(null);
      setTimeout(() => maybeAskLeadWith({ ...l, name }), 350);
      setBusy(false);
      return;
    }
    if (field === "phone" && !looksLikeQuestion(text)) {
      const digits = text.replace(/\D/g, "").slice(-10);
      if (/^[6-9]\d{9}$/.test(digits)) {
        setLead((p) => ({ ...p, phone: digits }));
        setPending(null);
        setTimeout(() => maybeAskLeadWith({ ...l, phone: digits }), 350);
      } else {
        bot("That doesn't look like a valid 10-digit mobile number — could you re-enter it?");
      }
      setBusy(false);
      return;
    }
    if (field === "email" && !looksLikeQuestion(text)) {
      const email = text.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setLead((p) => ({ ...p, email }));
        setPending(null);
        await saveLead({ ...l, email });
      } else {
        bot("That email doesn't look right — could you re-enter it?");
      }
      setBusy(false);
      return;
    }

    // Regular question (also when the visitor answers a lead prompt with a
    // question — answer first, re-ask later).
    await ask(text);
    const n = stateRef.current.answered + 1;
    setAnswered(n);
    setTimeout(() => maybeAskLead(n), 600);
    setBusy(false);
  }

  // Variant of maybeAskLead that uses just-updated values (state not yet flushed).
  function maybeAskLeadWith(l: { name?: string; phone?: string; email?: string }) {
    if (stateRef.current.leadSaved) return;
    if (l.name && !l.phone) {
      setPending("phone");
      bot(`Thanks, ${l.name}! Could you share your mobile number? Our workspace experts will reach out with the right options.`);
    } else if (l.name && l.phone && !l.email) {
      setPending("email");
      bot("And lastly, your work email? We'll send the details across.");
    }
  }

  return (
    <>
      {/* launcher */}
      {!open && (
        <button
          type="button"
          onClick={openChat}
          aria-label="Chat with us"
          className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand-accent text-brand-paper shadow-[0_10px_30px_-8px_rgba(56,105,99,0.7)] transition-transform hover:scale-105"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </button>
      )}

      {/* panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 flex h-[560px] w-[min(24rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-brand-line bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)]">
          {/* header */}
          <div className="flex items-center gap-3 bg-brand-accent px-4 py-3 text-brand-paper">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-sm font-semibold">TS</span>
            <div className="min-w-0">
              <p className="text-sm font-medium leading-tight">Table Space Assistant</p>
              <p className="text-[11px] text-white/75">Answers from tablespace.com</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="ml-auto rounded p-1 text-white/85 hover:bg-white/15">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {/* messages */}
          <div className="flex-1 space-y-3 overflow-y-auto bg-brand-surface px-3 py-4">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-md bg-brand-accent text-brand-paper"
                      : "rounded-bl-md border border-brand-line bg-white text-brand-ink"
                  }`}
                >
                  {m.text}
                  {m.links?.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 block text-xs font-medium text-brand-accent-dark underline underline-offset-2"
                    >
                      {l.label} →
                    </a>
                  ))}
                  {m.link && (
                    <a href={m.link} className="mt-1.5 block text-xs font-medium text-brand-accent-dark underline underline-offset-2">
                      {m.linkLabel ?? "Learn more"} →
                    </a>
                  )}
                </div>
              </div>
            ))}
            {/* typing indicator while the assistant composes a reply */}
            {busy && msgs[msgs.length - 1]?.role === "user" && (
              <div className="flex justify-start" aria-live="polite">
                <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-brand-line bg-white px-3.5 py-3">
                  <span className="sr-only">Assistant is typing…</span>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 rounded-full bg-brand-slate/50 motion-reduce:animate-none"
                      style={{ animation: `ts-chat-dot 1.2s ${i * 0.18}s infinite ease-in-out` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* quick replies on a fresh chat */}
            {msgs.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => void handleSend(q)}
                    className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs font-medium text-brand-slate hover:border-brand-accent hover:text-brand-accent"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); void handleSend(); }}
            className="flex items-center gap-2 border-t border-brand-line bg-white p-2.5"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                pending === "name" ? "Your name…"
                : pending === "phone" ? "10-digit mobile number…"
                : pending === "email" ? "Your email…"
                : "Ask about workspaces, locations…"
              }
              className="min-w-0 flex-1 rounded-full border border-brand-line px-4 py-2.5 text-sm outline-hidden focus:border-brand-accent"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-accent text-brand-paper disabled:opacity-40"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
